<template>
    <div class="new-owners p-2">
        <div class="row">
            <div class="col-12">
                <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-3">
                    <div>
                        <h5 class="title mt-2 mb-1">{{ pageTitle }}</h5>
                        <p class="text-muted mb-0">{{ pageSubtitle }}</p>
                    </div>
                    <div class="d-flex align-items-center mt-2 mt-md-0">
                        <select v-model.number="limit" class="form-select form-select-sm me-2 select-native mb-0">
                            <option :value="25">25 lignes</option>
                            <option :value="50">50 lignes</option>
                            <option :value="100">100 lignes</option>
                        </select>
                        <button type="button" class="btn btn-sm btn-outline-secondary btn-square" @click="fetchOwnersActivity">
                            <feather type="refresh-cw" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <LoaderComponent v-if="loading" />
        <template v-else>
            <div v-if="!isWithoutActiveLicensesPage" class="new-owners-metrics">
                <div class="metric">
                    <span>Nouvelles inscriptions</span>
                    <strong>{{ newOwners.length }}</strong>
                </div>
                <div class="metric priority">
                    <span>Sans établissement</span>
                    <strong>{{ newOwnersWithoutRestaurants.length }}</strong>
                </div>
                <div class="metric">
                    <span>Avec licence active</span>
                    <strong>{{ newOwnersWithActiveLicenses.length }}</strong>
                </div>
            </div>

            <owner-activity-table
                v-if="isWithoutActiveLicensesPage"
                title="Clients inactifs"
                subtitle="Les licences campaigns ne sont pas prises en compte ici."
                empty-text="Aucun client inactif."
                :owners="ownersWithoutActiveLicenses"
                show-canceled-date
                variant="warning" />

            <owner-activity-table
                v-if="!isWithoutActiveLicensesPage"
                title="Inscriptions récentes sans établissement"
                empty-text="Aucune inscription récente sans établissement."
                :owners="newOwnersWithoutRestaurants"
                :show-restaurants="false"
                show-new-badge
                class="mb-4" />

            <owner-activity-table
                v-if="!isWithoutActiveLicensesPage"
                title="Dernières inscriptions"
                empty-text="Aucune inscription récente."
                :owners="newOwners"
                show-new-badge />
        </template>
    </div>
</template>

<script>
import LoaderComponent from "../../components/LoaderComponent.vue";

const OwnerActivityTable = {
    name: "OwnerActivityTable",
    props: {
        owners: {
            type: Array,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        subtitle: {
            type: String,
            default: "",
        },
        emptyText: {
            type: String,
            required: true,
        },
        showRestaurants: {
            type: Boolean,
            default: true,
        },
        showCanceledDate: {
            type: Boolean,
            default: false,
        },
        showNewBadge: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: "default",
        },
    },
    methods: {
        count(value) {
            return Number.parseInt(value || 0, 10);
        },
        displayOwnerName(owner) {
            const fullname = `${owner.firstname || ""} ${owner.lastname || ""}`.trim();

            return owner.company || fullname || owner.email || `Client #${owner.id}`;
        },
        restaurantsLabel(owner) {
            if (!owner.restaurants_summary) {
                return "Aucun";
            }

            return owner.restaurants_summary.split(" | ").join(", ");
        },
        statusLabel(owner) {
            if (this.count(owner.active_paid_licenses_count) > 0) {
                return "Licence active";
            }

            if (this.count(owner.active_free_licenses_count) > 0) {
                return "Licence gratuite";
            }

            if (this.count(owner.restaurants_count) > 0) {
                return "Sans licence active";
            }

            return "Inscrit seulement";
        },
        statusClass(owner) {
            if (this.count(owner.active_paid_licenses_count) > 0) {
                return "text-bg-success";
            }

            if (this.count(owner.active_free_licenses_count) > 0) {
                return "text-bg-info";
            }

            if (this.count(owner.restaurants_count) > 0) {
                return "text-bg-warning";
            }

            return "text-bg-secondary";
        },
        formatDate(date, withTime = false) {
            if (!date) {
                return "-";
            }

            return this.$parent.displayDate(date, withTime ? this.$parent.DATETIME_SHORT : this.$parent.DATE_SHORT);
        },
    },
    template: `
        <div class="activity-section" :class="'activity-section-' + variant">
            <div class="d-flex align-items-center justify-content-between mb-2">
                <div>
                    <h6 class="mb-0">{{ title }}</h6>
                    <p v-if="subtitle" class="text-muted small mb-0 mt-1">{{ subtitle }}</p>
                </div>
                <span class="text-muted small">{{ owners.length }} compte(s)</span>
            </div>
            <div v-if="owners.length === 0" class="empty-state">
                {{ emptyText }}
            </div>
            <div v-else class="table-responsive">
                <table class="table table-sm table-striped border-bottom mb-0">
                    <thead>
                        <tr>
                            <th>Date d'inscription</th>
                            <th>Client</th>
                            <th>Email</th>
                            <th>Dernière connexion</th>
                            <th v-if="showRestaurants">Établissement(s)</th>
                            <th>Licences</th>
                            <th v-if="showCanceledDate">Dernière annulation</th>
                            <th>Statut</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="owner in owners" :key="owner.id">
                            <td>{{ formatDate(owner.created_at) }}</td>
                            <td>
                                <strong>{{ displayOwnerName(owner) }}</strong>
                                <span v-if="showNewBadge" class="badge text-bg-primary new-owner-badge ms-1">NEW</span>
                                <div class="text-muted small">#{{ owner.id }}</div>
                                <div v-if="owner.tel" class="text-muted small">{{ owner.tel }}</div>
                            </td>
                            <td>{{ owner.email || "-" }}</td>
                            <td>{{ formatDate(owner.last_login_at, true) }}</td>
                            <td v-if="showRestaurants">
                                {{ restaurantsLabel(owner) }}
                                <div class="text-muted small">{{ count(owner.restaurants_count) }} établissement(s)</div>
                            </td>
                            <td>
                                <span v-if="owner.active_licenses_summary">{{ owner.active_licenses_summary }}</span>
                                <span v-else>Aucune active hors campaigns</span>
                                <div v-if="count(owner.canceled_licenses_count) > 0" class="text-muted small">
                                    {{ count(owner.canceled_licenses_count) }} annulée(s)
                                </div>
                            </td>
                            <td v-if="showCanceledDate">{{ formatDate(owner.last_license_canceled_at || owner.last_canceled_at, true) }}</td>
                            <td>
                                <span class="badge" :class="statusClass(owner)">{{ statusLabel(owner) }}</span>
                            </td>
                            <td class="text-end">
                                <router-link
                                    :to="{ name: 'admin.ownerDetails', params: { owner_id: owner.id } }"
                                    class="btn btn-sm btn-outline-secondary btn-square">
                                    <feather type="eye" />
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `,
};

export default {
    name: "NewOwners",
    props: {
        view: {
            type: String,
            default: "newOwners",
        },
    },
    data() {
        return {
            loading: false,
            limit: 50,
            newOwners: [],
            ownersWithoutActiveLicenses: [],
        };
    },
    computed: {
        newOwnersWithoutRestaurants() {
            return this.newOwners.filter((owner) => Number.parseInt(owner.restaurants_count || 0, 10) === 0);
        },
        newOwnersWithActiveLicenses() {
            return this.newOwners.filter((owner) => Number.parseInt(owner.active_licenses_count || 0, 10) > 0);
        },
        isWithoutActiveLicensesPage() {
            return this.view === "withoutActiveLicenses";
        },
        pageTitle() {
            return this.isWithoutActiveLicensesPage ? "Clients inactifs" : "Nouveaux clients";
        },
        pageSubtitle() {
            return this.isWithoutActiveLicensesPage
                ? "Comptes clients inactifs, hors module campaigns."
                : "Suivi des inscriptions récentes, surtout celles sans établissement.";
        },
    },
    methods: {
        fetchOwnersActivity() {
            this.loading = true;

            this.httpGet(`/api/admin/owners/onboarding?limit=${this.limit}`)
                .then((response) => {
                    if (response === false) {
                        return;
                    }

                    this.newOwners = response.data.data.new_owners || [];
                    this.ownersWithoutActiveLicenses = response.data.data.owners_without_active_licenses || [];
                })
                .finally(() => (this.loading = false));
        },
    },
    watch: {
        limit() {
            this.fetchOwnersActivity();
        },
    },
    components: {
        LoaderComponent,
        OwnerActivityTable,
    },
    created() {
        this.fetchOwnersActivity();
    },
};
</script>

<style>
.new-owners {
    width: 100%;
    max-width: 1360px;
    margin-right: auto;
    margin-left: auto;
}

.new-owners-metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 20px;
}

.metric {
    padding: 14px 16px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.metric span {
    display: block;
    color: #627184;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
}

.metric strong {
    display: block;
    margin-top: 6px;
    color: #182b3c;
    font-size: 1.55rem;
    line-height: 1;
}

.metric.priority {
    border-color: #f0b25c;
}

.metric.warning {
    border-color: #e07a7a;
}

.activity-section {
    padding: 14px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.activity-section-warning {
    border-color: #e6a55f;
    background: #fffaf3;
}

.activity-section h6 {
    color: #182b3c;
    font-size: 0.98rem;
    font-weight: 700;
}

.empty-state {
    padding: 18px;
    border: 1px dashed #ced8e1;
    border-radius: 6px;
    color: #627184;
    text-align: center;
}

.badge {
    white-space: nowrap;
}

.new-owner-badge {
    font-size: 0.62rem;
    letter-spacing: 0;
    vertical-align: text-top;
}

@media (max-width: 991.98px) {
    .new-owners-metrics {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 575.98px) {
    .new-owners-metrics {
        grid-template-columns: 1fr;
    }
}
</style>
